import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { apiErrorFrom, networkError } from './errors';
import { env } from '@/lib/env';
import { gates } from '@/app/gates';

/**
 * The one HTTP client. Paths are relative because the API is same-origin in every environment
 * (Cloudflare routes `/api/*` in deployed builds, Vite proxies it locally — §20), so production
 * needs no CORS by design.
 */

type TokenSource = () => Promise<string | null>;
let tokenSource: TokenSource = async () => null;

/** The session installs the token source once it knows how the user is signed in. */
export function setTokenSource(source: TokenSource) {
  tokenSource = source;
}

const headers: Middleware = {
  async onRequest({ request }) {
    // Read by the server's version gate, which answers 426 when the build is too old.
    request.headers.set('x-app-version', env.appVersion);
    // Selects the web app's own minimum in the API's version gate (`min_web_app_version`).
    request.headers.set('x-app-client', 'web');
    request.headers.set('accept', 'application/json');
    const token = await tokenSource();
    if (token) request.headers.set('authorization', `Bearer ${token}`);
    return request;
  },
};

const failures: Middleware = {
  async onResponse({ response }) {
    if (response.ok) return response;
    let body: unknown = null;
    try {
      body = await response.clone().json();
    } catch {
      // A non-JSON error body (a proxy page) keeps the status-derived message.
    }
    const error = apiErrorFrom(response, body);
    // Fired before the error reaches the caller so the gates act whatever the caller does.
    if (error.kind === 'upgradeRequired') gates.getState().setUpdateRequired();
    if (error.kind === 'legal') gates.getState().setLegalRequired();
    throw error;
  },
  onError({ error }) {
    return networkError(error);
  },
};

export const api = createClient<paths>({ baseUrl: '' });
api.use(headers, failures);

/**
 * Unwraps an openapi-fetch result. Failures already throw in the middleware, so a missing `data`
 * here is a 204 or an empty body.
 */
export async function call<T>(promise: Promise<{ data?: T; error?: unknown }>): Promise<T> {
  const { data } = await promise;
  return data as T;
}
